import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import TableSortLabel from '@mui/material/TableSortLabel';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { useData } from './DataProvider';

export default function CopyCerts({copycertUrl, buttonProps = {} }) {
  const [open, setOpen] = useState(false);
  const { copycerts, setCopyCerts } = useData();
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [orderBy, setOrderBy] = useState('Thumbprint');
  const [order, setOrder] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');

  // Helper function to highlight matching text
  const highlightText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <Box 
          key={index} 
          component="span" 
          sx={{ 
            backgroundColor: '#ffeb3b', 
            color: '#000',
            fontWeight: 'bold',
            padding: '0 2px',
            borderRadius: '2px'
          }}
        >
          {part}
        </Box>
      ) : part
    );
  };

  useEffect(() => {
    if (!copycertUrl) {
      setError('No copycert.json URL provided.');
      return;
    }
    fetch(copycertUrl)
      .then(res => {
        if (!res.ok) throw new Error('Failed to load copycert.json from blob storage');
        return res.json();
      })
      .then(data => {
        const arr = Object.values(data);
        setCopyCerts(arr);
        if (arr.length > 0) setSelected(arr[0]);
        setError(null);
      })
      .catch(e => setError(e.message));
  }, [copycertUrl, setCopyCerts]);

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrder('asc');
    }
  };

  const sortedCerts = React.useMemo(() => {
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    
    // First filter by search term
    const filteredCerts = copycerts.filter(cert => (
      (cert.Thumbprint && cert.Thumbprint.toLowerCase().includes(searchTerm.toLowerCase()))
        || (cert.Location && cert.Location.toLowerCase().includes(searchTerm.toLowerCase()))
        || (cert.Store && cert.Store.toLowerCase().includes(searchTerm.toLowerCase()))
        || (cert.Subject && cert.Subject.toLowerCase().includes(searchTerm.toLowerCase()))
        || (cert.Issuer && cert.Issuer.toLowerCase().includes(searchTerm.toLowerCase()))
        || (cert.SerialNumber && cert.SerialNumber.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    );
    
    return [...filteredCerts].sort((a, b) => {
      let aValue = a[orderBy] ?? '';
      let bValue = b[orderBy] ?? '';
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }
      return order === 'asc' ? collator.compare(aValue, bValue) : collator.compare(bValue, aValue);
    });
  }, [copycerts, orderBy, order, searchTerm]);

  // Button to open dialog
  return (
    <React.Fragment>
      <Button
        size="small"
        startIcon={<InfoOutlinedIcon sx={{ fontSize: 18 }} />}
        onClick={() => setOpen(true)}
        sx={{ py: 0.5, minHeight: 28 }}
        {...buttonProps}
      >
        Cert Info
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { width: '90vw', height: '90vh', maxWidth: 'none', maxHeight: 'none' } }}
      >
        <DialogContent sx={{ p: 3, height: 'calc(90vh - 70px)', boxSizing: 'border-box' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Certificate Info</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box p={0} display="flex" flexDirection="row" gap={4} height="calc(100% - 80px)">
            <Box flex={2} minHeight={0} display="flex" flexDirection="column">
              <Box mb={2} display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Total certs: {copycerts.length} {searchTerm && `(${sortedCerts.length} matching)`}
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search certs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 250 }}
                />
              </Box>
              <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={orderBy === 'Thumbprint' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'Thumbprint'}
                          direction={orderBy === 'Thumbprint' ? order : 'asc'}
                          onClick={() => handleSort('Thumbprint')}
                        >
                          Thumbprint
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={orderBy === 'Location' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'Location'}
                          direction={orderBy === 'Location' ? order : 'asc'}
                          onClick={() => handleSort('Location')}
                        >
                          Location
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={orderBy === 'Store' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'Store'}
                          direction={orderBy === 'Store' ? order : 'asc'}
                          onClick={() => handleSort('Store')}
                        >
                          Store
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedCerts.map((cert, idx) => (
                      <TableRow
                        key={cert.Thumbprint || idx}
                        hover
                        selected={selected && selected.Thumbprint === cert.Thumbprint}
                        onClick={() => setSelected(cert)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ maxWidth: 320, wordBreak: 'break-all' }}>
                          {highlightText(cert.Thumbprint, searchTerm)}
                        </TableCell>
                        <TableCell align="right">{cert.Location ? cert.Location : ''}</TableCell>
                        <TableCell align="right">{cert.Store ? cert.Store : ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <Box flex={1} minWidth={320} minHeight={0} display="flex" flexDirection="column" height="100%">
              {selected ? (
                <Paper sx={{ p: 2, flex: 1, maxHeight: '100%', overflow: 'auto', minHeight: 0 }}>
                  <List dense>
                    {Object.entries(selected).map(([key, value]) => (
                      <ListItem key={key} alignItems="flex-start" disablePadding>
                        <ListItemText
                          primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>{key}</Typography>}
                          secondary={Array.isArray(value)
                            ? value.length > 0
                              ? value.map((v, i) => <span key={i}>{v}<br /></span>)
                              : <em>None</em>
                            : (typeof value === 'boolean' ? (value ? 'true' : 'false') : String(value))}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              ) : (
                <Typography color="text.secondary">Select a certificate to see details</Typography>
              )}
            </Box>
          </Box>
          {error && <Typography color="error" sx={{ p: 2 }}>{error}</Typography>}
          {!copycerts.length && !error && <Typography sx={{ p: 2 }}>Loading copycert info...</Typography>}
        </DialogContent>
        <Divider sx={{ width: '100%' }} />
        <Box sx={{ mt: 2, mb: 3, px: 3, textAlign: 'right' }}>
          <Button onClick={() => setOpen(false)} variant="contained">Close</Button>
        </Box>
      </Dialog>
    </React.Fragment>
  );
}
